/**
 * Frozen columns and a locked header row for the busy list screens — the Job Sheets list, Worker
 * Register, Job Lines, Audit Log, the payroll run's statutory grid and the Casual Timesheet
 * (client request, 14 Oct 2026: "a standard feature that lock column and lock row so it's
 * flexible for user to adjust it"). Any other table opts in the same way.
 *
 * ── Two halves, only one of them a setting ─────────────────────────────────
 * The HEADER ROW is always locked — nobody wants column titles to scroll away, so it is not
 * offered as a choice. The COLUMNS are the user's: `Freeze columns` is a checklist of the table's
 * columns, and the ones ticked stay put while the rest scroll sideways. A frozen column has to
 * sit at the LEFT edge, so a ticked column MOVES there (the client's call, same day: "move to
 * left if they lock it") — ticked columns in their usual order, then the rest in theirs — and
 * goes back to its place when unticked. At most `FREEZE_MAX`. It ships with nothing ticked (the
 * client's choice) and is remembered per browser, per table — the text-size and column-width
 * argument (`text-size.ts`, `column-widths.ts`): it belongs to a person at a screen, moves no
 * money, and nothing on the server reads it. Every storage access is wrapped.
 *
 * The first cut froze "the first N columns" (Excel's Freeze Panes); a browser that stored a count
 * then reads it as the first N columns of the checklist, so nobody's choice is lost.
 *
 * Rows other than the header are not frozen: in a list the header is the row you lose, and a
 * "freeze N data rows" setting would be a choice nobody can explain.
 *
 * ── Why each table scrolls in its OWN box ───────────────────────────────────
 * The page cannot hold a header for us. The shell's `<main>` is `overflow-x: hidden` (§3.9, no
 * sideways page scroll), which makes it a scrolling box that never scrolls — the window does —
 * so nothing `sticky` inside it ever sticks to the window (the job sheet's LIVE SPLIT footer is
 * `position: fixed` for exactly this reason; see `sheets/$date/$teamId.tsx`). `overflow-x: clip`
 * would fix that at the root, but it would also switch ON the Worker Register's and Teams'
 * side panels, which have always said `sticky` and never stuck: the worker profile is taller
 * than a screen, and stuck at the top its lower half would be reachable only at the end of the
 * list. So, like the Attendance board, each table here scrolls BOTH ways inside a box one
 * screen tall, and its header holds at the top of that box.
 *
 * ── Two table kinds ─────────────────────────────────────────────────────────
 * An antd `<Table>` does it all itself: `scroll={{ x, y: LIST_SCROLL_Y }}` gives the body its
 * own box under a fixed header, and `pinAntdColumns` moves the ticked columns left and marks
 * them `fixed: 'left'`. A `<DenseTable>`'s rows are its caller's markup, so the CALLER puts the
 * ticked columns first (`pinColumns`, the same order for its header and every row) and passes
 * `stickyHeader` and `freezeColumns={keys.length}`; a keyboard grid offers only its read-only
 * columns, so moving one never reorders the cells the arrow keys walk.
 */
import { useCallback, useState } from 'react';

/** The most columns a user may freeze. Past three, a frozen block crowds out the rest. */
export const FREEZE_MAX = 3;

/** A column the user may tick: its column `key` and the name the checklist shows. */
export interface FreezableColumn {
  key: string;
  title: string;
}

/**
 * antd's `scroll.y` for a list table: the BODY's maximum height, under its own fixed header —
 * one screen less the top bar, ~24px of air and the ~40px header row, so scrolled up under the
 * top bar the whole table is on screen. `--cm-viewport-h`, never `100vh` (the text-size zoom).
 * In a full view (`FullView.tsx`) `--cm-fv-avail` — the height measured below the box — wins.
 */
export const LIST_SCROLL_Y = 'var(--cm-fv-avail, calc(var(--cm-viewport-h) - var(--cm-header-h) - 64px))';

const STORAGE_PREFIX = 'cm.freezeColumns.';

/**
 * Stored text → the ticked keys, in the checklist's order. A JSON array keeps the keys the table
 * still has (a column renamed or removed in a later release drops out); a bare count is the first
 * cut's "first N columns"; anything else is nothing ticked, the shipped default. Pure.
 */
export function parseFrozenKeys(
  raw: string | null | undefined,
  allowed: readonly string[],
  max: number = FREEZE_MAX,
): string[] {
  if (raw === null || raw === undefined || raw === '') return [];
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    return n <= max ? allowed.slice(0, n) : [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return orderedKeys(parsed.filter((k): k is string => typeof k === 'string'), allowed, max);
}

/**
 * `keys` widened to the LEADING BLOCK of `allowed` that ends at the last one ticked — tick
 * `Worker` and `#` comes with it. For a keyboard grid whose freezable columns are already its
 * first ones (the day sheet's JOBS BOOKED and crew grids): moving a lone ticked column left of
 * an unticked one would reorder the cells the arrow keys walk, so instead nothing moves and the
 * checklist shows every column that is actually frozen. Pure.
 */
export function leadingKeys(keys: readonly string[], allowed: readonly string[]): string[] {
  const last = allowed.reduce((at, k, i) => (keys.includes(k) ? i : at), -1);
  return allowed.slice(0, last + 1);
}

/** `keys` reduced to the allowed ones, once each, in the checklist's order, at most `max`. Pure. */
export function orderedKeys(keys: readonly string[], allowed: readonly string[], max: number = FREEZE_MAX): string[] {
  const wanted = new Set(keys);
  return allowed.filter((k) => wanted.has(k)).slice(0, max);
}

/** The table's ticked column keys and their setter, remembered per browser under `storageKey`. */
export function useFrozenColumns(
  storageKey: string,
  freezable: readonly FreezableColumn[],
  max: number = FREEZE_MAX,
): [string[], (keys: readonly string[]) => void] {
  const allowed = freezable.map((c) => c.key);
  const [keys, setKeys] = useState<string[]>(() => {
    try {
      return parseFrozenKeys(window.localStorage.getItem(STORAGE_PREFIX + storageKey), allowed, max);
    } catch {
      return [];
    }
  });
  const allowedSig = allowed.join('|');
  const set = useCallback(
    (next: readonly string[]) => {
      const clean = orderedKeys(next, allowedSig.split('|'), max);
      try {
        if (clean.length === 0) window.localStorage.removeItem(STORAGE_PREFIX + storageKey);
        else window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(clean));
      } catch {
        /* Storage refused: the choice still applies until the page is left. */
      }
      setKeys(clean);
    },
    [storageKey, allowedSig, max],
  );
  return [keys, set];
}

/**
 * `items` with the ticked ones moved to the front — ticked in their usual order, then the rest in
 * theirs. Pure; the caller renders its header AND every row in this one order.
 */
export function pinColumns<T>(items: readonly T[], keys: readonly string[], keyOf: (item: T) => string | undefined): T[] {
  if (keys.length === 0) return [...items];
  const ticked = new Set(keys);
  const isPinned = (item: T) => {
    const k = keyOf(item);
    return k !== undefined && ticked.has(k);
  };
  return [...items.filter(isPinned), ...items.filter((item) => !isPinned(item))];
}

/**
 * antd columns with the ticked ones moved to the left and marked `fixed: 'left'`. Pure. A column
 * is known by its `key`, else its string `dataIndex` — antd's own fallback, and how Medical Claims
 * names most of its columns. A column its screen already fixes (the Job Sheets list's clear
 * button, fixed right) keeps that and is never ticked, so it stays where it is.
 */
export function pinAntdColumns<C extends { key?: unknown; dataIndex?: unknown; fixed?: unknown }>(
  columns: readonly C[],
  keys: readonly string[],
): C[] {
  const keyOf = (c: C) => {
    if (c.fixed) return undefined;
    if (typeof c.key === 'string') return c.key;
    return typeof c.dataIndex === 'string' ? c.dataIndex : undefined;
  };
  const ticked = new Set(keys);
  return pinColumns(columns, keys, keyOf).map((c) => {
    const k = keyOf(c);
    return k !== undefined && ticked.has(k) ? { ...c, fixed: 'left' as const } : c;
  });
}
